import { inject } from '@angular/core';
import { IEnrollmentTagType, IUserTagType } from '@lb/api/mylb/interfaces';
import { patchState, signalStoreFeature, withMethods, withState } from '@ngrx/signals';
import { SettingsService } from '@lb/api/mylb/services/settings.service';
import { forkJoin, take } from 'rxjs';
import { MDAlertService } from '@melodic-ui/components';

export type IAdminState = {
	enrollmentTagTypes: IEnrollmentTagType[];
	userTagTypes: IUserTagType[];
	adminStateLoaded: boolean;
	adminStateLoading: boolean;
};

export const initAdminState: IAdminState = {
	enrollmentTagTypes: [],
	userTagTypes: [],
	adminStateLoaded: false,
	adminStateLoading: false
};

export function adminState() {
	return signalStoreFeature(
		withState<IAdminState>(initAdminState),
		withMethods((state, settingsService = inject(SettingsService), alertService = inject(MDAlertService)) => {
			const showError = (title: string, error: any) => {
				const message = error?.error?.message ?? 'An unexpected error occurred. Please try again later.';

				console.error(error);
				alertService.addAlert({ title, message, type: 'error', duration: 8000 });
			};
			const loadTagTypes = () => {
				patchState(state, { adminStateLoading: true });

				forkJoin({
					enrollmentTagTypes: settingsService.getEnrollmentTagTypes(),
					userTagTypes: settingsService.getUserTagTypes()
				})
					.pipe(take(1))
					.subscribe({
						next: (result) => {
							patchState(state, {
								enrollmentTagTypes: result.enrollmentTagTypes,
								userTagTypes: result.userTagTypes,
								adminStateLoaded: true,
								adminStateLoading: false
							});
						},
						error: (error) => {
							showError('Error loading tag types', error);
							patchState(state, { adminStateLoading: false });
						}
					});
			};

			const createEnrollmentTagType = (tagType: IEnrollmentTagType) => {
				patchState(state, { adminStateLoading: true });
				settingsService
					.createEnrollmentTagType(tagType)
					.pipe(take(1))
					.subscribe({
						next: () => loadTagTypes(),
						error: (error) => {
							showError('Error creating enrollment tag type', error);
							patchState(state, { adminStateLoading: false });
						}
					});
			};

			const updateEnrollmentTagType = (id: number, tagType: IEnrollmentTagType) => {
				patchState(state, { adminStateLoading: true });
				settingsService
					.updateEnrollmentTagType(id, tagType)
					.pipe(take(1))
					.subscribe({
						next: () => loadTagTypes(),
						error: (error) => {
							showError('Error updating enrollment tag type', error);
							patchState(state, { adminStateLoading: false });
						}
					});
			};

			const deleteEnrollmentTagType = (id: number) => {
				patchState(state, { adminStateLoading: true });
				settingsService
					.deleteEnrollmentTagType(id)
					.pipe(take(1))
					.subscribe({
						next: () => loadTagTypes(),
						error: (error) => {
							showError('Error deleting enrollment tag type', error);
							patchState(state, { adminStateLoading: false });
						}
					});
			};

			const createUserTagType = (tagType: IUserTagType) => {
				patchState(state, { adminStateLoading: true });
				settingsService
					.createUserTagType(tagType)
					.pipe(take(1))
					.subscribe({
						next: () => loadTagTypes(),
						error: (error) => {
							showError('Error creating user tag type', error);
							patchState(state, { adminStateLoading: false });
						}
					});
			};

			const updateUserTagType = (id: number, tagType: IUserTagType) => {
				patchState(state, { adminStateLoading: true });
				settingsService
					.updateUserTagType(id, tagType)
					.pipe(take(1))
					.subscribe({
						next: () => loadTagTypes(),
						error: (error) => {
							showError('Error updating user tag type', error);
							patchState(state, { adminStateLoading: false });
						}
					});
			};

			const deleteUserTagType = (id: number) => {
				patchState(state, { adminStateLoading: true });
				settingsService
					.deleteUserTagType(id)
					.pipe(take(1))
					.subscribe({
						next: () => loadTagTypes(),
						error: (error) => {
							showError('Error deleting user tag type', error);
							patchState(state, { adminStateLoading: false });
						}
					});
			};

			return {
				loadTagTypes,
				createEnrollmentTagType,
				updateEnrollmentTagType,
				deleteEnrollmentTagType,
				createUserTagType,
				updateUserTagType,
				deleteUserTagType
			};
		})
	);
}
